import { CommonModule } from '@angular/common';
import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { License, LicenseApi } from '@venecia/apis/identity';
import { ErrorMessageService, MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import { Column, RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-license-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, ResponsiveTable, Spinner],
  templateUrl: './license-grid.html',
  styleUrl: './license-grid.scss',
})
export class LicenseGrid implements OnInit, OnChanges {
  private readonly licenseApi = inject(LicenseApi);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  readonly accountId = input<number | null>(null);
  readonly reloadVersion = input<number>(0);
  readonly detailRequested = output<License>();
  readonly totalChanged = output<number>();

  readonly licenses = signal<readonly License[]>([]);
  readonly loading = signal(false);

  readonly pageNumber = signal(1);
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);
  readonly statusCell = viewChild<TemplateRef<{ $implicit: License }>>('statusCell');

  readonly trackByLicense = (_: number, license: License) =>
    license.id ??
    `${license.account?.id ?? ''}-${license.application?.id ?? ''}-${license['active-from']}`;

  readonly columns = computed<readonly Column<License>[]>(() => [
    {
      id: 'application',
      header: this.translateService.instant('pages.licenses.fields.application'),
      value: (license) => license.application?.name ?? '',
      nowrap: true,
      width: 40
    },
    {
      id: 'active-from',
      header: this.translateService.instant('pages.licenses.fields.active-from'),
      value: (license) => license['active-from'],
      nowrap: true,
      width: 20
    },
    {
      id: 'active-to',
      header: this.translateService.instant('pages.licenses.fields.active-to'),
      value: (license) => license['active-to'],
      nowrap: true,
      width: 20
    },
    {
      id: 'status',
      header: this.translateService.instant('pages.licenses.fields.status'),
      cellTemplate: this.statusCell(),
      nowrap: true,
      width: 10
    },
  ]);

  readonly actions = computed<readonly RowAction<License>[]>(() => [
    {
      id: 'details',
      label: this.translateService.instant('pages.licenses.actions.details'),
      icon: 'fa-solid fa-pen-to-square',
    },
  ]);

  ngOnInit(): void {
    void this.loadLicenses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    if (!reloadVersionChange || reloadVersionChange.firstChange) {
      return;
    }

    this.pageNumber.set(1);
    void this.loadLicenses();
  }

  onActionSelected(ev: { actionId: string; item: License }): void {
    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }

    this.pageNumber.set(page);
    void this.loadLicenses();
  }

  private async loadLicenses(): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.licenseApi.search({
          payload: this.buildSearchPayload(),
        }),
      );

      this.licenses.set(response.body.data ?? []);
      this.totalItems.set(response.body.total ?? 0);
      this.totalChanged.emit(this.totalItems());
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.licenses.load-error'));
    } finally {
      this.loading.set(false);
    }
  }

  private buildSearchPayload(): Record<string, unknown> {
    const accountId = this.accountId();

    return {
      page: this.pageNumber() - 1,
      size: this.pageSize(),
      ...(accountId !== null
        ? {
            filters: [
              {
                field: 'account.id',
                value: accountId,
                comparative: 'eq',
              },
            ],
          }
        : {}),
    };
  }

  isActive(license: License): boolean {
    const now = Date.now();
    const startsAt = this.toTimestamp(license['active-from'], 'start');
    const endsAt = this.toTimestamp(license['active-to'], 'end');

    return startsAt !== null && endsAt !== null && startsAt <= now && now <= endsAt;
  }

  private toTimestamp(value: string, boundary: 'start' | 'end'): number | null {
    const suffix = boundary === 'start' ? 'T00:00:00' : 'T23:59:59.999';
    const timestamp = Date.parse(`${value}${value.includes('T') ? '' : suffix}`);
    return Number.isNaN(timestamp) ? null : timestamp;
  }
}
